import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { environment } from 'src/assets/env/environment';

/**
 * DOCU-1376: scripts on demand in the documentation view (DOCU-1365, API DOCU-1374).
 * Hand-written calls (endpoints not in the generated client yet); auth via token interceptor.
 * HttpErrorInterceptor leaves these URLs alone — the caller shows errors itself
 * (see isScriptRequestUrl).
 */

export type ScriptRunStatus = 'queued' | 'running' | 'ok' | 'notApplicable' | 'error';

/** A script offered on one documentation. */
export interface AuditScriptDto {
  key: string;
  displayName: string;
  description?: string | null;
  /** Queued or running run of this script on this documentation. */
  openRunId?: string | null;
}

export interface ScriptRunDto {
  id: string;
  auditId: string;
  scriptKey: string;
  displayName?: string | null;
  eventType: string;
  status: ScriptRunStatus;
  exitCode?: number | null;
  /** Script stdout, for the user. */
  output?: string | null;
  /** Script stderr. The API sends it only outside Production. */
  debugOutput?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
}

const STATUS_BY_INDEX: ScriptRunStatus[] = ['queued', 'running', 'ok', 'notApplicable', 'error'];

/** The API serialises enums as camelCase strings; a numeric value is mapped defensively. */
export function normalizeRunStatus(status: unknown): ScriptRunStatus {
  if (typeof status === 'number') return STATUS_BY_INDEX[status] ?? 'error';
  const s = String(status ?? '');
  const match = STATUS_BY_INDEX.find((v) => v.toLowerCase() === s.toLowerCase());
  return match ?? 'error';
}

export function isFinalRunStatus(status: ScriptRunStatus): boolean {
  return status === 'ok' || status === 'notApplicable' || status === 'error';
}

/** URLs of this feature, so HttpErrorInterceptor shows no toast for them. */
export function isScriptRequestUrl(url: string): boolean {
  return /\/api\/v1\/(audit\/[^/?]+\/(scripts|script-runs)|script-runs\/)/.test(url);
}

@Injectable({ providedIn: 'root' })
export class ScriptService {
  private readonly base = `${environment.API_BASE_URL}/api/v1`;

  constructor(private readonly http: HttpClient) {}

  /**
   * Scripts offered on this documentation. Empty = no button (switch off, nothing fits the
   * template, or the role may not run scripts). Any error also means "no button" — an API
   * without DOCU-1374 answers 404 here.
   */
  getAuditScripts(auditId: string): Observable<AuditScriptDto[]> {
    return this.http.get<AuditScriptDto[]>(`${this.base}/audit/${auditId}/scripts`).pipe(
      map((list) => list ?? []),
      catchError(() => of([])),
    );
  }

  /**
   * Queues a run. 202 = new run; 409 = this script already has an open run on this
   * documentation, the body is that run — the caller simply follows it.
   */
  startRun(auditId: string, scriptKey: string): Observable<ScriptRunDto> {
    return this.http.post<ScriptRunDto>(`${this.base}/audit/${auditId}/script-runs`, { scriptKey }).pipe(
      catchError((error: HttpErrorResponse) =>
        error.status === 409 && error.error?.id ? of(error.error as ScriptRunDto) : throwError(() => error)),
      map((run) => ({ ...run, status: normalizeRunStatus(run.status) })),
    );
  }

  getRun(runId: string): Observable<ScriptRunDto> {
    return this.http.get<ScriptRunDto>(`${this.base}/script-runs/${runId}`).pipe(
      map((run) => ({ ...run, status: normalizeRunStatus(run.status) })),
    );
  }
}
